import dayjs from 'dayjs';
import { filter, flatMap, isEmpty, map, pipe, prop, sum } from 'remeda';
import type { DebtorBalance } from 'shared/hooks/use-load-debtor-balance';
import { isPaid } from 'shared/utils/invoice-status';

export function computeDebtorBalance(debtorBalance: DebtorBalance) {
  const now = dayjs();
  const oneYearAgo = now.subtract(1, 'year');

  const invoicesLastYear = pipe(
    debtorBalance.invoices,
    filter(invoice => dayjs(invoice.issueDate).isAfter(oneYearAgo)),
  );

  const creditNotesLastYear = pipe(
    debtorBalance.creditNotes,
    filter(creditNote => dayjs(creditNote.issueDate).isAfter(oneYearAgo)),
  );

  const totalCreditNotesSum = debtorBalance.creditNotes.reduce(
    (acc, creditNote) => acc + creditNote.totalTvac * -1,
    0,
  );

  const remainingLateFees = debtorBalance.invoices.reduce(
    (acc, invoice) => acc + invoice.remainingLateFees,
    0,
  );

  const totalLateFeesBilled = debtorBalance.invoices.reduce(
    (acc, invoice) => acc + invoice.lateFees,
    0,
  );

  const totalBilled = debtorBalance.invoices.reduce((acc, invoice) => acc + invoice.totalTvac, 0);

  const annualTurnover =
    invoicesLastYear.reduce((acc, invoice) => acc + invoice.totalTvac, 0) -
    creditNotesLastYear.reduce((acc, creditNote) => acc - creditNote.totalTvac, 0);

  const openInvoices = debtorBalance.invoices.filter(invoice => !isPaid(invoice));

  const unmatchedPaymentsSum = debtorBalance.payments.reduce(
    (acc, payment) => acc + payment.remainingBalance,
    0,
  );

  const matchedPaymentsSum =
    debtorBalance.invoices.reduce((acc, invoice) => {
      // HACK: In some cases, we receive a payment that includes late fees, but the late fees are later
      // discarded. To avoid overcontributing to the total payments sum, we only contribute the initial total
      // of the invoice if the remaining balance is 0.

      // Hack 2: It turns out that in some cases, the invoice will have both payments and credit notes.
      // We thus must remove the credit note amount in those cases.
      if (invoice.remainingBalance + invoice.lateFees === 0 && !isEmpty(invoice.matchedPayments)) {
        return (
          acc +
          invoice.totalTvac -
          invoice.creditNotes.reduce(
            (accumulation, creditNote) => accumulation + creditNote.amount,
            0,
          )
        );
      }

      const total = invoice.matchedPayments.reduce(
        (totalAcc, payment) => totalAcc + (payment.isLoss ? 0 : payment.amount),
        0,
      );

      return acc + total;
    }, 0) +
    debtorBalance.creditNotes.reduce((acc, invoice) => {
      const total =
        invoice.payments?.reduce((totalAcc, payment) => totalAcc + payment.amount, 0) ?? 0;

      return acc + total;
    }, 0);

  const totalPaymentsSum = unmatchedPaymentsSum + matchedPaymentsSum;

  const lostAmount = pipe(
    debtorBalance.invoices,
    flatMap(prop('matchedPayments')),
    filter(prop('isLoss')),
    map(prop('amount')),
    sum(),
  );

  const adjustForEpsilon = (value: number): number => {
    const FINANCIAL_EPSILON = 1e-10;
    return Math.abs(value) < FINANCIAL_EPSILON ? 0 : value;
  };

  const remainingBalance = adjustForEpsilon(
    totalBilled + totalLateFeesBilled - totalPaymentsSum - totalCreditNotesSum,
  );

  return {
    annualTurnover,
    lostAmount,
    openInvoices,
    remainingBalance,
    remainingLateFees,
    totalBilled,
    totalCreditNotesSum,
    totalLateFeesBilled,
    totalPaymentsSum,
  };
}
